import { Dayjs } from 'dayjs';

import type { SessionDraft } from './sessionDraft';

export type SessionOverlapPair = [number, number];

const range = (end: number): number[] =>
  Array.from({ length: end }, (_, value) => value);

export const isSessionEndDateDisabled = (
  candidate: Dayjs,
  startAt: Dayjs | null,
): boolean => !!startAt && candidate.isBefore(startAt, 'day');

export const getDisabledSessionEndHours = (
  selectedDate: Dayjs | null,
  startAt: Dayjs | null,
): number[] =>
  selectedDate && startAt && selectedDate.isSame(startAt, 'day')
    ? range(startAt.hour())
    : [];

export const getDisabledSessionEndMinutes = (
  selectedDate: Dayjs | null,
  startAt: Dayjs | null,
  selectedHour: number,
): number[] =>
  selectedDate &&
  startAt &&
  selectedDate.isSame(startAt, 'day') &&
  selectedHour === startAt.hour()
    ? range(startAt.minute() + 1)
    : [];

export const withSessionStartAt = (
  draft: SessionDraft,
  startAt: Dayjs | null,
): SessionDraft => ({
  ...draft,
  startAt,
  endAt:
    startAt && draft.endAt && !draft.endAt.isAfter(startAt)
      ? null
      : draft.endAt,
});

export const withSessionEndAt = (
  draft: SessionDraft,
  endAt: Dayjs | null,
): SessionDraft => ({
  ...draft,
  endAt: endAt && draft.startAt && !endAt.isAfter(draft.startAt) ? null : endAt,
});

export const findSessionOverlapPairs = (
  sessions: Pick<SessionDraft, 'startAt' | 'endAt'>[],
): SessionOverlapPair[] => {
  const pairs: SessionOverlapPair[] = [];

  sessions.forEach((first, firstIndex) => {
    if (!first.startAt || !first.endAt) {
      return;
    }

    for (
      let secondIndex = firstIndex + 1;
      secondIndex < sessions.length;
      ++secondIndex
    ) {
      const second = sessions[secondIndex];
      if (
        second.startAt &&
        second.endAt &&
        first.startAt.isBefore(second.endAt) &&
        second.startAt.isBefore(first.endAt)
      ) {
        pairs.push([firstIndex, secondIndex]);
      }
    }
  });

  return pairs;
};

export const formatSessionDateTimeRange = (
  session: Pick<SessionDraft, 'startAt' | 'endAt'>,
): string => {
  if (!session.startAt || !session.endAt) {
    return '';
  }

  const start = session.startAt.format('D MMM YYYY, HH:mm');
  const end = session.startAt.isSame(session.endAt, 'day')
    ? session.endAt.format('HH:mm')
    : session.endAt.format('D MMM YYYY, HH:mm');

  const separator = session.startAt.isSame(session.endAt, 'day') ? '–' : ' – ';
  return `${start}${separator}${end}`;
};
